import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import useSideNav from "../services/LandingPage/useSideNav";
import ConfirmLogoutModal from "../utils/ConfirmLogoutModal";
import logoImage from "../images/LandingPageImage/title.svg";
import { LogOut } from "lucide-react";

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const userData = JSON.parse(sessionStorage.getItem("user"));
  const userID = userData?.userid;
  const { modules = [] } = useSideNav(userID);

  const allowedModules = modules;

  const currentPath = (location.pathname || "").toLowerCase();

  const activeModule = modules.find((module) => {
    if (!module?.module_url) return false;

    const moduleRoot = module.module_url
      .toLowerCase()
      .split("/")
      .slice(0, 2)
      .join("/");

    return currentPath.startsWith(moduleRoot);
  });

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    navigate("/");
  };

  return (
    <div className="flex h-screen montserrat-font-2">
      {/* ================= LEFT ICON SIDEBAR ================= */}
      <div className="w-[65px] bg-white border-r flex flex-col h-screen">
        {/* Logo */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <NavLink to="/dashboard" className="flex flex-col items-center py-4">
            <img
              src={logoImage}
              alt="Logo"
              className="h-12 mb-2 mt-5 cursor-pointer"
            />
          </NavLink>

          {/* Modules */}
          <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col">
            {allowedModules
              .filter((module) => module?.module_url)
              .map((module) => {
                const isActive = activeModule?.module_url === module.module_url;

                return (
                  <NavLink
                    key={module.id}
                    to={module.module_url}
                    className={`relative flex flex-col items-center py-2 transition ${
                      isActive
                        ? "bg-[#2871fa] text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <img
                      src={
                        isActive
                          ? module.module_selected_icon
                          : module.module_active_icon
                      }
                      className="w-5 h-5 mb-1"
                      alt={module.module_name}
                    />

                    <span className="text-[10px] text-center">
                      {module.module_name}
                    </span>
                  </NavLink>
                );
              })}
          </div>
        </div>

        {/* Logout */}
        <div className="flex justify-center border-t py-3">
          <ConfirmLogoutModal
            onConfirm={handleLogout}
            trigger={
              <button className="flex flex-col items-center text-[#2871fa] hover:bg-gray-100 w-full py-3">
                <LogOut
                  className="w-5 h-5 mb-1 opacity-80 hover:opacity-100 hover:text-red-500 transition-all cursor-pointer"
                  strokeWidth={2}
                />
                <span className="text-[10px]">Logout</span>
              </button>
            }
          />
        </div>
      </div>

      {/* ================= INTERNAL NAV ================= */}

      {activeModule && activeModule?.module_name !== "Dashboard" && (
        <div className="w-[75px] bg-white border-r flex flex-col h-screen">
          {/* Arrow Row */}
          <div className="h-[70px] flex items-center justify-center border-b-2 border-gray-300 mb-2 bg-white p-5">
            <button
              onClick={() => navigate(-1)}
              className="text-2xl text-gray-600 hover:text-black"
            >
              ←
            </button>
          </div>

          {/* Services */}
          <div className="flex flex-col text-xs flex-1 overflow-y-auto hide-scrollbar">
            {(activeModule.services || [])
              .filter((service) => service?.service_url)
              .map((service) => {
                const servicePath = (service.service_url || "").toLowerCase();
                const isActive = currentPath === servicePath;

                return (
                  <NavLink
                    key={service.id}
                    to={service.service_url}
                    className={`relative flex flex-col items-center justify-center py-2 transition ${
                      isActive
                        ? " text-gray-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {isActive && (
                      <>
                        {/* Right Border */}
                        <div className="absolute right-0 top-0 h-full w-[3px] bg-[#2871fa]" />

                        {/* Top Border */}
                        <div className="absolute left-0 top-0 h-[3px] w-full bg-[#2871fa]" />

                        {/* Bottom Border */}
                        <div className="absolute bottom-0 left-0 h-[3px] w-full bg-[#2871fa]" />
                      </>
                    )}
                    {service.service_active_icon && (
                      <img
                        src={
                          isActive
                            ? service.service_active_icon
                            : service.service_icon ||
                              service.service_active_icon
                        }
                        alt={service.service_name}
                        className="w-5 h-5 mb-2"
                      />
                    )}
                    <span className="text-center leading-tight">
                      {service.service_name}
                    </span>
                  </NavLink>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationBar;
